import { getAssignableClients } from './cohortClientEligibilityService';
import { getCohortFilterLabels, sortCohortsByOrder } from './cohortService';

/** @typedef {import('../entities/CareModelCohort.types').CareModelCohort} CareModelCohort */
/** @typedef {import('../entities/CareModelCohort.types').Client} Client */
/** @typedef {
 *   import('../entities/CareModelCohortImpactReview.types').CareModelCohortImpactMatch
 * } CareModelCohortImpactMatch */
/** @typedef {
 *   import('../entities/CareModelCohortImpactReview.types').ImpactedCareModelCohortAssignment
 * } ImpactedCareModelCohortAssignment */

/**
 * @param {CareModelCohort[]} cohorts
 * @returns {CareModelCohort[]}
 */
export function sortCohortsForImpactReview(cohorts = []) {
  return sortCohortsByOrder(cohorts);
}

/**
 * @param {CareModelCohort} cohort
 * @param {Object} hydratedFilters
 * @returns {CareModelCohortImpactMatch}
 */
export function buildImpactedCohortMatch(cohort, hydratedFilters = {}) {
  return {
    cohort,
    matchedLabels: getCohortFilterLabels(hydratedFilters),
  };
}

/**
 * A client is considered impacted if, after recalculation, they have exactly one suggested cohort,
 * and it differs from their current assignment. Clients not eligible for assignment are skipped.
 *
 * @param {{
 *   clients?: Client[],
 *   getMatchesForClient: (client: Client) => CareModelCohortImpactMatch[],
 * }} params
 * @returns {ImpactedCareModelCohortAssignment[]}
 */
export function buildImpactedClientsReview({ clients = [], getMatchesForClient }) {
  return getAssignableClients(clients).reduce((acc, client) => {
    const matches = getMatchesForClient(client);

    if (matches.length !== 1) {
      return acc;
    }

    const suggestedMatch = matches[0];
    const currentCohortId = client.careModelCohort || null;

    if (suggestedMatch.cohort.id === currentCohortId) {
      return acc;
    }

    acc.push({
      client,
      currentCohortId,
      suggestedCohort: suggestedMatch.cohort,
      matchedLabels: suggestedMatch.matchedLabels,
      selected: true,
    });

    return acc;
  }, []);
}
